// src/pages/edu/EduLegal.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";

const KIND = "legal";
const ACCEPT = ".xls,.xlsx,.pdf,.hwp,.hwpx,.ppt,.pptx,.zip";

/* 파일 URL을 API 서버 기준 절대경로로 변환 */
function resolveFileUrl(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return base + url;
}

export default function EduLegal() {
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({ period: "", title: "", content: "", link: "", category: "" });
  const [newCat, setNewCat] = useState("");
  const [formMaterials, setFormMaterials] = useState([]);   // 교육자료
  const [formCerts, setFormCerts] = useState([]);           // 수료증
  const [formAttendees, setFormAttendees] = useState([]);   // 수강생명단

  const [me, setMe] = useState(null);
  const rank = parseInt(me?.position_rank, 10);
  const canEdit = rank === 2 || rank === 3;
  const canDelete = rank === 2;

  const fetchList = () => {
    setLoading(true);
    const params = { kind: KIND };
    if (search) params.search = search;
    if (catFilter) params.category = catFilter;
    api.get("education/courses/", { params })
      .then((r) => setList(r.data.results || r.data))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    api.get("education/categories/")
      .then((r) => setCategories(r.data.results || r.data))
      .catch(() => setCategories([]));
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => {
    api.get("/auth/me/").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [catFilter]);

  const onSearch = (e) => { e.preventDefault(); fetchList(); };

  const addCategory = () => {
    const name = newCat.trim();
    if (!name) return;
    api.post("education/categories/", { name })
      .then(() => { setNewCat(""); fetchCategories(); })
      .catch((err) => alert(err?.response?.data?.name?.[0] || "분류 추가 실패"));
  };

  const createCourse = () => {
    if (!form.title.trim()) { alert("강의명을 입력하세요."); return; }
    api.post("education/courses/", {
      kind: KIND,
      period: form.period,
      title: form.title,
      content: form.content,
      link: form.link,
      category: form.category || null,
    })
      .then((r) => {
        const newId = r.data.id;
        return Promise.all([
          uploadFilesTo(newId, "material", formMaterials),
          uploadFilesTo(newId, "certificate", formCerts),
          uploadFilesTo(newId, "attendee", formAttendees),
        ]);
      })
      .then(() => {
        setForm({ period: "", title: "", content: "", link: "", category: "" });
        setFormMaterials([]);
        setFormCerts([]);
        setFormAttendees([]);
        fetchList();
      })
      .catch((err) => alert(err?.response?.data?.file?.[0] || "등록 실패"));
  };

  const deleteCourse = (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    api.delete(`education/courses/${id}/`).then(fetchList);
  };

  const updateCourse = (id, payload) => {
    return api.patch(`education/courses/${id}/`, payload)
      .then(fetchList)
      .catch(() => alert("수정 실패"));
  };

  const uploadFilesTo = (courseId, fileType, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return Promise.resolve();
    return Promise.all(
      arr.map((file) => {
        const fd = new FormData();
        fd.append("course", courseId);
        fd.append("file_type", fileType);
        fd.append("file", file);
        return api.post("education/files/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      })
    );
  };

  const uploadFile = (courseId, fileType, files) => {
    uploadFilesTo(courseId, fileType, files)
      .then(fetchList)
      .catch((err) => alert(err?.response?.data?.file?.[0] || "업로드 실패 (엑셀/PDF/한글/PPT/ZIP만 가능)"));
  };

  const deleteFile = (fileId) => {
    if (!window.confirm("파일을 삭제하시겠습니까?")) return;
    api.delete(`education/files/${fileId}/`).then(fetchList);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 16 }}>법정의무교육 (현장근로자)</h2>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ───────── 왼쪽: 등록 영역 ───────── */}
        <div style={{ flex: "0 0 360px", maxWidth: 360, minWidth: 300 }}>
          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>교육 등록</div>

            <div style={{ display: "grid", gap: 8 }}>
              {/* 분류 선택 + 새 분류 추가 (한 줄) */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  style={{ ...inp, flex: "0 0 auto", width: 130 }}>
                  <option value="">분류 선택</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
                <input value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  placeholder="새 분류"
                  style={{ flex: "1 1 auto", minWidth: 0, padding: "8px 8px", border: "1px solid #ccc", borderRadius: 6 }} />
                <button type="button" onClick={addCategory} style={btn("#888", 13)}>추가</button>
              </div>

              <input placeholder="교육기간 (예: 2026.06.01 ~ 06.30)" value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })} style={inp} />
              <input placeholder="강의명" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} style={inp} />
              <textarea placeholder="강의 내용" value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6}
                style={{ ...inp, resize: "vertical", minHeight: 120 }} />
              <input placeholder="교육링크 (https://...)" value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })} style={inp} />

              <FormFilePicker label="교육자료" files={formMaterials} setFiles={setFormMaterials} />
              <FormFilePicker label="수료증" files={formCerts} setFiles={setFormCerts} />
              <FormFilePicker label="수강생명단" files={formAttendees} setFiles={setFormAttendees} />

              <button type="button" onClick={createCourse} style={btn("#2d6cdf")}>저장</button>
            </div>
          </div>
        </div>

        {/* ───────── 오른쪽: 검색 + 목록 ───────── */}
        <div style={{ flex: "1 1 0", minWidth: 320 }}>
          <form onSubmit={onSearch} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "nowrap" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="전체 검색 (교육기간·강의명·내용·분류 등)"
              style={{ flex: "1 1 auto", minWidth: 0, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6 }} />
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
              style={{ flex: "0 0 auto", width: 130, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6 }}>
              <option value="">전체 분류</option>
              {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
            <button type="submit" style={btn("#2d6cdf")}>검색</button>
          </form>

          {loading && <p style={{ color: "#888" }}>불러오는 중…</p>}
          {!loading && list.length === 0 && <p style={{ color: "#888" }}>등록된 교육이 없습니다.</p>}

          {list.map((c, idx) => (
            <CourseCard key={c.id} course={c} index={list.length - idx}
              categories={categories}
              canEdit={canEdit} canDelete={canDelete}
              onDelete={deleteCourse} onUpdate={updateCourse}
              onUpload={uploadFile} onDeleteFile={deleteFile} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course, index, categories = [], canEdit, canDelete, onDelete, onUpdate, onUpload, onDeleteFile }) {
  const materials = course.files?.filter((f) => f.file_type === "material") || [];
  const certs = course.files?.filter((f) => f.file_type === "certificate") || [];
  const attendees = course.files?.filter((f) => f.file_type === "attendee") || [];

  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({
    period: course.period || "",
    title: course.title || "",
    content: course.content || "",
    link: course.link || "",
    category: course.category || "",
    author_name: course.author_name || "",
  });

  const saveEdit = () => {
    onUpdate(course.id, {
      period: edit.period,
      title: edit.title,
      content: edit.content,
      link: edit.link,
      category: edit.category || null,
      author_name: edit.author_name,
    }).then(() => setEditing(false));
  };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "#2d6cdf" }}>#{index}</span>
          {course.category_name && (<span style={tag}>{course.category_name}</span>)}
          <span style={{ fontSize: 12, color: "#999" }}>{course.created_at?.slice(0, 10)}</span>
          {!editing && course.author_name && (
            <span style={{ fontSize: 12, color: "#666" }}>작성자: {course.author_name}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {canEdit && !editing && (
            <button onClick={() => setEditing(true)} style={btn("#2d6cdf", 12)}>수정</button>
          )}
          {editing && (
            <>
              <button onClick={saveEdit} style={btn("#27ae60", 12)}>저장</button>
              <button onClick={() => setEditing(false)} style={btn("#999", 12)}>취소</button>
            </>
          )}
          {canDelete && !editing && (
            <button onClick={() => onDelete(course.id)} style={btn("#e74c3c", 12)}>삭제</button>
          )}
        </div>
      </div>

      {editing ? (
        <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>작성자</span>
            <input value={edit.author_name} onChange={(e) => setEdit({ ...edit, author_name: e.target.value })}
              style={{ ...inp, width: 160 }} />
            <select value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })}
              style={{ ...inp, width: 140 }}>
              <option value="">분류 선택</option>
              {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <input value={edit.period} onChange={(e) => setEdit({ ...edit, period: e.target.value })}
            placeholder="교육기간" style={inp} />
          <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            placeholder="강의명" style={inp} />
          <textarea value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })}
            rows={4} placeholder="강의 내용" style={{ ...inp, resize: "vertical" }} />
          <input value={edit.link} onChange={(e) => setEdit({ ...edit, link: e.target.value })}
            placeholder="교육링크" style={inp} />
        </div>
      ) : (
        <>
          {course.period && (
            <div style={{ fontSize: 13, color: "#2c7", fontWeight: 600, marginBottom: 2 }}>📅 교육기간: {course.period}</div>
          )}
          <h3 style={{ margin: "4px 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{course.title}</h3>
          <p style={{ color: "#444", margin: "4px 0 10px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{course.content}</p>

          {course.link && (
            <div style={{ marginBottom: 10 }}>
              <a href={course.link} target="_blank" rel="noreferrer"
                style={{ ...btnStyleBase("#8e44ad", 13), textDecoration: "none" }}>🔗 링크</a>
            </div>
          )}
        </>
      )}

      <FileSection title="교육자료" files={materials} editing={editing}
        onUpload={(f) => onUpload(course.id, "material", f)} onDeleteFile={onDeleteFile} />
      <FileSection title="수료증" files={certs} editing={editing}
        onUpload={(f) => onUpload(course.id, "certificate", f)} onDeleteFile={onDeleteFile} />
      <FileSection title="수강생명단" files={attendees} editing={editing}
        onUpload={(f) => onUpload(course.id, "attendee", f)} onDeleteFile={onDeleteFile} />
    </div>
  );
}

function FormFilePicker({ label, files, setFiles }) {
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };
  const removeAt = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>{label}</span>
        <label style={{ ...btnStyleBase("#34495e", 12), cursor: "pointer" }}>
          파일 선택
          <input type="file" accept={ACCEPT} multiple style={{ display: "none" }} onChange={onPick} />
        </label>
      </div>
      {files.length > 0 && (
        <ul style={{ margin: "4px 0 0", padding: 0, listStyle: "none" }}>
          {files.map((f, i) => (
            <li key={i} style={{ fontSize: 12, color: "#333", padding: "2px 0",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span title={f.name}>📎 {f.name}</span>
              <button type="button" onClick={() => removeAt(i)}
                style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer", marginLeft: 4 }}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileSection({ title, files, editing, onUpload, onDeleteFile }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{title}</span>
        {editing && (
          <label style={{ ...btnStyleBase("#34495e", 12), cursor: "pointer" }}>
            업로드
            <input type="file" accept={ACCEPT} multiple style={{ display: "none" }}
              onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
      {files.length === 0 ? (
        <small style={{ color: "#aaa" }}>첨부 없음</small>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
          {files.map((f) => {
            const fname = f.original_name || (f.file_url ? decodeURIComponent(f.file_url.split("/").pop()) : "(파일명 없음)");
            return (
            <li key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0", flexWrap: "wrap" }}>
              <a href={resolveFileUrl(f.file_url)} download={fname} title={fname}
                style={{ ...btnStyleBase("#2d6cdf", 12), textDecoration: "none" }}>⬇ 다운로드</a>
              <span title={fname} style={{ color: "#333", wordBreak: "break-all" }}>{fname}</span>
              {editing && (
                <button onClick={() => onDeleteFile(f.id)}
                  style={{ border: "none", background: "none", color: "#bbb", cursor: "pointer", flexShrink: 0 }}>×</button>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function btnStyleBase(bg, fontSize = 14) {
  return { background: bg, color: "#fff", border: "none", borderRadius: 6,
    padding: "6px 14px", fontSize, cursor: "pointer", fontWeight: 600,
    whiteSpace: "nowrap", flexGrow: 0, flexShrink: 0, width: "auto",
    display: "inline-flex", alignItems: "center", justifyContent: "center" };
}
function btn(bg, fontSize = 14) { return btnStyleBase(bg, fontSize); }

const card = { border: "1px solid #e5e5e5", borderRadius: 10, padding: 16,
  marginBottom: 16, background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" };
const inp = { padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, width: "100%", boxSizing: "border-box" };
const tag = { background: "#eef3ff", color: "#2d6cdf", borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 };